import { useAuth } from "@clerk/clerk-react";
import {
  Backdrop,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { AdminBentoCategoryFull } from "schemas/admin";

import Alert from "components/Alert";
import StyledPage from "components/Styled/Page";
import { fetcherAuth } from "utils/api";
import { useAlert } from "utils/useAlert";

import EditCategoryDrawer, { CategoryDrawerMode } from "./EditCategoryDrawer";

const AdminCategories = () => {
  const { getToken } = useAuth();
  const [message, severity, setAlert, closeAlert] = useAlert();

  const [loading, setLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<AdminBentoCategoryFull[]>([]);
  const [search, setSearch] = useState<string>("");
  const [drawerMode, setDrawerMode] = useState<CategoryDrawerMode | null>(null);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        "/api/admin/bento-categories/full",
        "GET",
      );
      setCategories(res.bentoCategories || []);
    } catch (error) {
      setAlert(error?.message || "Failed to load categories", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const q = search.trim().toLowerCase();
  const filteredCategories = q
    ? categories.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.parentCategoryNames.some((p) => p.toLowerCase().includes(q)),
      )
    : categories;

  const upsertCategory = (saved: AdminBentoCategoryFull) => {
    setCategories((prev) => {
      const exists = prev.some((c) => c.id === saved.id);
      if (exists) {
        return prev.map((c) => (c.id === saved.id ? saved : c));
      }
      return [...prev, saved].sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  const handleSaved = (saved: AdminBentoCategoryFull) => {
    upsertCategory(saved);
    setDrawerMode({ kind: "edit", category: saved });
    setAlert("Category saved", "success");
  };

  const renderFlagChips = (category: AdminBentoCategoryFull) => {
    const chips: string[] = [];
    if (category.autoArchive) chips.push("auto-archive");
    if (category.doNotDisplay) chips.push("hidden");
    if (category.useInOnboarding) chips.push("onboarding");
    if (category.useInVaOnboarding) chips.push("va-onboarding");
    if (category.isCreatorAgency) chips.push("creator-agency");
    if (category.isLocalOnly) chips.push("local-only");
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {chips.map((label) => (
          <Chip key={label} label={label} size="small" />
        ))}
      </Stack>
    );
  };

  return (
    <StyledPage
      content={
        <>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loading}
          >
            <CircularProgress />
          </Backdrop>

          <Grid container sx={{ mt: 2 }} spacing={1} alignItems="center">
            <Grid item xs>
              <Typography variant="h3" gutterBottom>
                <strong>Bento Categories</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and edit the same fields that the CSV upload overwrites.
                Subcategories can be added or removed from inside a parent
                category's drawer. Shopify mappings are still CSV-only.
              </Typography>
            </Grid>
            <Grid item xs="auto">
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={() => setDrawerMode({ kind: "createTopLevel" })}
                >
                  New Category
                </Button>
                <Button variant="outlined" onClick={fetchCategories}>
                  Refresh
                </Button>
              </Stack>
            </Grid>
          </Grid>

          <Grid container sx={{ my: 2 }} spacing={1}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Search by name or parent"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Grid>
          </Grid>

          <TableContainer sx={{ maxHeight: "70vh" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Parents</TableCell>
                  <TableCell>Standardized Tags</TableCell>
                  <TableCell>Flags</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCategories.map((category) => (
                  <TableRow key={category.id} hover>
                    <TableCell>{category.id}</TableCell>
                    <TableCell>
                      <strong>{category.name}</strong>
                    </TableCell>
                    <TableCell>
                      {category.parentCategoryNames.join(", ") || "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 320,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {(category.standardizedTags || []).join(", ") || "—"}
                    </TableCell>
                    <TableCell>{renderFlagChips(category)}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          setDrawerMode({ kind: "edit", category })
                        }
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredCategories.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 2, textAlign: "center" }}
                      >
                        No categories found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <EditCategoryDrawer
            mode={drawerMode}
            allCategories={categories}
            onClose={() => setDrawerMode(null)}
            onSaved={handleSaved}
            setAlert={setAlert}
          />

          <Alert
            message={message}
            severity={severity}
            closeAlert={closeAlert}
          />
        </>
      }
    />
  );
};

export default AdminCategories;
